// Compress dialog for the oversized image import guard. Fork-only.
//
// Renders the phase the guard is in: the oversized file, the compress
// progress, the preview of the compressed result, or the smallest size
// reached. Every visible string goes through the translation layer.

import { useEffect, useState } from "react";

import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";

import { useDrawolaT } from "../storage/i18n";

import { IMAGE_IMPORT_MAX_BYTES } from "./config";

import type {
  ImageImportRecompressAction,
  ImageImportState,
} from "./useImageImportGuard";

import "./imageImport.scss";

export type ImageImportDialogProps = {
  state: ImageImportState;
  onAccept: () => void;
  onCancel: () => void;
  onRecompress: (action: ImageImportRecompressAction) => void;
};

type Translator = (
  key: string,
  replacement?: { [key: string]: string | number },
) => string;

// Sizes are user-visible, so they go through the translation layer too: only
// the number is computed here, the unit label comes from the locale file.
const formatSize = (t: Translator, bytes: number): string => {
  if (bytes < 1024) {
    return t("imageImport.sizeB", { value: bytes });
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return t("imageImport.sizeKB", { value: Number(kb.toFixed(1)) });
  }
  return t("imageImport.sizeMB", { value: Number((kb / 1024).toFixed(1)) });
};

const formatName = (t: Translator, format: string): string => {
  if (format === "image/webp") {
    return t("imageImport.formatWebp");
  }
  if (format === "image/jpeg") {
    return t("imageImport.formatJpeg");
  }
  return format;
};

export const ImageImportDialog = ({
  state,
  onAccept,
  onCancel,
  onRecompress,
}: ImageImportDialogProps) => {
  const drawolaT = useDrawolaT();

  const result =
    state.phase === "preview" || state.phase === "couldNotFit"
      ? state.result
      : null;

  // Object URL for the preview image, revoked when it changes or closes.
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!result) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(result.file);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [result]);

  if (state.phase === "idle") {
    return null;
  }

  switch (state.phase) {
    case "tooLarge":
      return (
        <Dialog
          onCloseRequest={onCancel}
          title={drawolaT("imageImport.tooLargeTitle")}
          size="small"
        >
          <div className="imageImportDialog">
            <p>
              {drawolaT("imageImport.tooLargeBody", {
                size: formatSize(drawolaT, state.sizeBytes),
                limit: formatSize(drawolaT, IMAGE_IMPORT_MAX_BYTES),
              })}
            </p>
            <div className="imageImportDialog__actions">
              <FilledButton
                size="large"
                label={drawolaT("imageImport.compress")}
                onClick={onAccept}
              />
              <FilledButton
                size="large"
                variant="outlined"
                color="muted"
                label={drawolaT("imageImport.cancelImport")}
                onClick={onCancel}
              />
            </div>
          </div>
        </Dialog>
      );

    case "working":
      return (
        <Dialog onCloseRequest={onCancel} title={false} size="small">
          <div className="imageImportDialog">
            <p className="imageImportDialog__note">
              {drawolaT("imageImport.working")}
            </p>
          </div>
        </Dialog>
      );

    case "preview":
      return (
        <Dialog
          onCloseRequest={onCancel}
          title={drawolaT("imageImport.previewTitle")}
          size="small"
        >
          <div className="imageImportDialog">
            {objectUrl && (
              <img
                className="imageImportDialog__preview"
                src={objectUrl}
                alt=""
              />
            )}
            <ul className="imageImportDialog__stats">
              <li>
                {drawolaT("imageImport.resultSize", {
                  size: formatSize(drawolaT, state.result.bytes),
                })}
              </li>
              <li>
                {drawolaT("imageImport.resultFormat", {
                  format: formatName(drawolaT, state.result.format),
                })}
              </li>
              <li>
                {drawolaT("imageImport.resultQuality", {
                  quality: `${Math.round(state.result.quality * 100)}%`,
                })}
              </li>
            </ul>
            {state.result.format === "image/jpeg" && (
              <p className="imageImportDialog__note">
                {drawolaT("imageImport.jpegTransparencyNote")}
              </p>
            )}
            <div className="imageImportDialog__actions">
              <FilledButton
                size="large"
                label={drawolaT("imageImport.useImage")}
                onClick={onAccept}
              />
              <FilledButton
                size="large"
                variant="outlined"
                label={drawolaT("imageImport.tryOtherFormat")}
                onClick={() => onRecompress("otherFormat")}
              />
              <FilledButton
                size="large"
                variant="outlined"
                color="muted"
                label={drawolaT("imageImport.smaller")}
                onClick={() => onRecompress("smaller")}
              />
              <FilledButton
                size="large"
                variant="outlined"
                color="muted"
                label={drawolaT("imageImport.cancelImport")}
                onClick={onCancel}
              />
            </div>
          </div>
        </Dialog>
      );

    case "couldNotFit":
      return (
        <Dialog
          onCloseRequest={onCancel}
          title={drawolaT("imageImport.couldNotFitTitle")}
          size="small"
        >
          <div className="imageImportDialog">
            {objectUrl && (
              <img
                className="imageImportDialog__preview"
                src={objectUrl}
                alt=""
              />
            )}
            <p className="imageImportDialog__note">
              {drawolaT("imageImport.couldNotFitBody", {
                size: formatSize(drawolaT, state.result.bytes),
                limit: formatSize(drawolaT, IMAGE_IMPORT_MAX_BYTES),
              })}
            </p>
            <div className="imageImportDialog__actions">
              {/* One format exhausting its ladder says nothing about the
                  other format, so "Try the other format" stays reachable. */}
              <FilledButton
                size="large"
                variant="outlined"
                label={drawolaT("imageImport.tryOtherFormat")}
                onClick={() => onRecompress("otherFormat")}
              />
              <FilledButton
                size="large"
                variant="outlined"
                color="muted"
                label={drawolaT("imageImport.smaller")}
                onClick={() => onRecompress("smaller")}
              />
              <FilledButton
                size="large"
                variant="outlined"
                color="muted"
                label={drawolaT("imageImport.cancelImport")}
                onClick={onCancel}
              />
            </div>
          </div>
        </Dialog>
      );
  }
};
